import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Save, Tag, X, Plus, List, AlertCircle } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
const steps = [{
  id: 1,
  name: 'Select Form'
}, {
  id: 2,
  name: 'Opt-In Email'
}, {
  id: 3,
  name: 'Mailing Lists'
}, {
  id: 4,
  name: 'Tags & Behavior'
}, {
  id: 5,
  name: 'Review'
}];
export function FormConfiguration() {
  const {
    templates,
    tags,
    newsletters,
    saveFormConfig,
    currentWorkspace,
    currentSubRegion,
    getUnconfiguredForms
  } = useWorkspace();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [newTag, setNewTag] = useState('');
  const availableForms = getUnconfiguredForms();
  const [config, setConfig] = useState({
    formId: '',
    templateId: '',
    targetNewsletterIds: [] as number[],
    tags: [] as string[],
    redirectUrl: '',
    confirmationMessage: 'Thank you for subscribing! Please check your email to confirm.',
    status: 'active' as const
  });
  const handleAddTag = (tagName: string) => {
    if (tagName.trim() && !config.tags.includes(tagName.trim())) {
      setConfig({
        ...config,
        tags: [...config.tags, tagName.trim()]
      });
    }
    setNewTag('');
  };
  const handleRemoveTag = (tag: string) => {
    setConfig({
      ...config,
      tags: config.tags.filter(t => t !== tag)
    });
  };
  const handleNewsletterToggle = (id: number) => {
    setConfig(prev => ({
      ...prev,
      targetNewsletterIds: prev.targetNewsletterIds.includes(id) ? prev.targetNewsletterIds.filter(n => n !== id) : [...prev.targetNewsletterIds, id]
    }));
  };
  const handleSave = () => {
    if (config.formId && config.templateId) {
      saveFormConfig({
        workspaceId: currentWorkspace.id,
        subRegion: currentSubRegion || undefined,
        formId: parseInt(config.formId),
        templateId: parseInt(config.templateId),
        targetNewsletterIds: config.targetNewsletterIds,
        tags: config.tags,
        redirectUrl: config.redirectUrl,
        confirmationMessage: config.confirmationMessage,
        status: config.status
      });
      navigate('/configurations');
    }
  };
  const selectedForm = availableForms.find(f => f.id === parseInt(config.formId));
  const selectedTemplate = templates.find(t => t.id === parseInt(config.templateId));
  const availableTags = tags.filter(t => !config.tags.includes(t.name));
  const selectedNewsletters = newsletters.filter(n => config.targetNewsletterIds.includes(n.id));
  return <div className="space-y-8 max-w-3xl mx-auto" data-id="element-1120">
      <div className="text-center" data-id="element-1121">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1122">
          Form Configuration
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-1123">
          Configure how form submissions are processed, tagged, and routed to
          mailing lists.
        </p>
      </div>

      {/* Progress Bar */}
      <div className="relative" data-id="element-1124">
        <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-1125">
          <div className="w-full border-t border-gray-200" data-id="element-1126" />
        </div>
        <div className="relative flex justify-between" data-id="element-1127">
          {steps.map(step => {
          const isComplete = step.id < currentStep;
          const isCurrent = step.id === currentStep;
          return <div key={step.id} className="flex flex-col items-center" data-id="element-1128">
                <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 bg-white ${isComplete ? 'border-blue-600 bg-blue-600 text-white' : isCurrent ? 'border-blue-600 text-blue-600' : 'border-gray-300 text-gray-500'}`} data-id="element-1129">
                  {isComplete ? <CheckCircle2 className="h-5 w-5" data-id="element-1130" /> : <span className="text-xs" data-id="element-1131">{step.id}</span>}
                </div>
                <span className={`mt-2 text-xs font-medium ${isCurrent ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-1132">
                  {step.name}
                </span>
              </div>;
        })}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8" data-id="element-1133">
        {currentStep === 1 && <div className="space-y-4" data-id="element-1134">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-1135">
              Select a Signup Form
            </h2>
            <p className="text-sm text-gray-500" data-id="element-1136">
              Which form should this configuration apply to?
            </p>

            {availableForms.length === 0 ? <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 flex items-start gap-3" data-id="element-1137">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-1138" />
                <div data-id="element-1139">
                  <p className="text-sm font-medium text-yellow-800" data-id="element-1140">
                    All forms are already configured
                  </p>
                  <p className="text-sm text-yellow-700 mt-1" data-id="element-1141">
                    Create a new form or delete an existing configuration to
                    proceed.
                  </p>
                </div>
              </div> : <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5" value={config.formId} onChange={e => setConfig({
          ...config,
          formId: e.target.value
        })} data-id="element-1142">
                <option value="" data-id="element-1143">Select a form...</option>
                {availableForms.map(f => <option key={f.id} value={f.id} data-id="element-1144">
                    {f.name} {f.subRegion ? `(${f.subRegion})` : ''}
                  </option>)}
              </select>}
          </div>}

        {currentStep === 2 && <div className="space-y-4" data-id="element-1145">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-1146">
              Select Opt-In Email Template
            </h2>
            <p className="text-sm text-gray-500" data-id="element-1147">
              Which email should be sent for double opt-in confirmation?
            </p>
            <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5" value={config.templateId} onChange={e => setConfig({
          ...config,
          templateId: e.target.value
        })} data-id="element-1148">
              <option value="" data-id="element-1149">Select a template...</option>
              {templates.map(t => <option key={t.id} value={t.id} data-id="element-1150">
                  {t.name} - "{t.subject}"
                </option>)}
            </select>
          </div>}

        {currentStep === 3 && <div className="space-y-4" data-id="element-1151">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-1152">
              Target Mailing Lists
            </h2>
            <p className="text-sm text-gray-500" data-id="element-1153">
              Which mailing lists should confirmed subscribers be added to?
            </p>

            <div className="space-y-2" data-id="element-1154">
              {newsletters.map(newsletter => <label key={newsletter.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer" data-id="element-1155">
                  <input type="checkbox" checked={config.targetNewsletterIds.includes(newsletter.id)} onChange={() => handleNewsletterToggle(newsletter.id)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-1156" />
                  <div className="flex-1" data-id="element-1157">
                    <div className="text-sm font-medium text-gray-900" data-id="element-1158">
                      {newsletter.name}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1159">
                      {newsletter.subscriberCount} subscribers •{' '}
                      {newsletter.description}
                    </div>
                  </div>
                </label>)}
            </div>

            {config.targetNewsletterIds.length === 0 && <div className="bg-blue-50 border border-blue-100 rounded-md p-3 text-sm text-blue-700" data-id="element-1160">
                <strong data-id="element-1161">Tip:</strong> Select at least one mailing list so
                confirmed subscribers are added somewhere.
              </div>}
          </div>}

        {currentStep === 4 && <div className="space-y-6" data-id="element-1162">
            <div data-id="element-1163">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-1164">
                Tags & Behavior
              </h2>
              <p className="text-sm text-gray-500" data-id="element-1165">
                Add tags to submissions and configure post-confirmation
                behavior.
              </p>
            </div>

            {/* Tags Section */}
            <div className="space-y-3" data-id="element-1166">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1167">
                <Tag className="inline h-4 w-4 mr-1" data-id="element-1168" />
                Submission Tags
              </label>
              <p className="text-xs text-gray-500" data-id="element-1169">
                Tags will be automatically applied to all submissions from this
                form.
              </p>

              {availableTags.length > 0 && <div className="space-y-2" data-id="element-1170">
                  <span className="text-xs text-gray-500" data-id="element-1171">
                    Select from existing tags:
                  </span>
                  <div className="flex flex-wrap gap-2" data-id="element-1172">
                    {availableTags.map(tag => <button key={tag.id} type="button" onClick={() => handleAddTag(tag.name)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200" data-id="element-1173">
                        <Plus className="h-3 w-3" data-id="element-1174" />
                        {tag.name}
                      </button>)}
                  </div>
                </div>}

              <div className="flex gap-2" data-id="element-1175">
                <input type="text" placeholder="Or enter a new tag name..." className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newTag} onChange={e => setNewTag(e.target.value)} onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAddTag(newTag))} data-id="element-1176" />
                <button type="button" onClick={() => handleAddTag(newTag)} disabled={!newTag.trim()} className="inline-flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 disabled:opacity-50" data-id="element-1177">
                  <Plus className="h-4 w-4" data-id="element-1178" />
                  Add
                </button>
              </div>

              {config.tags.length > 0 && <div className="space-y-2" data-id="element-1179">
                  <span className="text-xs text-gray-500" data-id="element-1180">Selected tags:</span>
                  <div className="flex flex-wrap gap-2" data-id="element-1181">
                    {config.tags.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-1182">
                        <Tag className="h-3 w-3" data-id="element-1183" />
                        {tag}
                        <button onClick={() => handleRemoveTag(tag)} className="text-blue-400 hover:text-blue-600" data-id="element-1184">
                          <X className="h-3 w-3" data-id="element-1185" />
                        </button>
                      </span>)}
                  </div>
                </div>}
            </div>

            {/* Redirect URL */}
            <div className="space-y-2" data-id="element-1186">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1187">
                Redirect URL (Optional)
              </label>
              <input type="url" placeholder="https://example.com/thank-you" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={config.redirectUrl} onChange={e => setConfig({
            ...config,
            redirectUrl: e.target.value
          })} data-id="element-1188" />
              <p className="text-xs text-gray-500" data-id="element-1189">
                Where to redirect after they click the confirmation link.
              </p>
            </div>

            {/* Confirmation Message */}
            <div className="space-y-2" data-id="element-1190">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1191">
                Confirmation Message
              </label>
              <textarea rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={config.confirmationMessage} onChange={e => setConfig({
            ...config,
            confirmationMessage: e.target.value
          })} data-id="element-1192" />
              <p className="text-xs text-gray-500" data-id="element-1193">
                Message shown after form submission (before email confirmation).
              </p>
            </div>
          </div>}

        {currentStep === 5 && <div className="space-y-6" data-id="element-1194">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-1195">
              Review Configuration
            </h2>

            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-1196">
              <div className="grid grid-cols-2 gap-4" data-id="element-1197">
                <div data-id="element-1198">
                  <span className="text-sm text-gray-500 block" data-id="element-1199">Form</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-1200">
                    {selectedForm?.name || '-'}
                  </span>
                </div>
                <div data-id="element-1201">
                  <span className="text-sm text-gray-500 block" data-id="element-1202">
                    Opt-In Email
                  </span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-1203">
                    {selectedTemplate?.name || '-'}
                  </span>
                </div>
                <div data-id="element-1204">
                  <span className="text-sm text-gray-500 block" data-id="element-1205">
                    Mailing Lists
                  </span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-1206">
                    {selectedNewsletters.length > 0 ? selectedNewsletters.map(n => n.name).join(', ') : 'None selected'}
                  </span>
                </div>
                <div data-id="element-1207">
                  <span className="text-sm text-gray-500 block" data-id="element-1208">Tags</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-1209">
                    {config.tags.length > 0 ? config.tags.join(', ') : 'No tags'}
                  </span>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4" data-id="element-1210">
              <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-1211">
                How it works
              </h3>
              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside" data-id="element-1212">
                <li data-id="element-1213">
                  User submits{' '}
                  <strong data-id="element-1214">{selectedForm?.name || 'the form'}</strong>
                </li>
                <li data-id="element-1215">
                  Submission is tagged with:{' '}
                  <strong data-id="element-1216">{config.tags.join(', ') || 'no tags'}</strong>
                </li>
                <li data-id="element-1217">
                  Opt-in email "
                  <strong data-id="element-1218">{selectedTemplate?.subject || '...'}</strong>" is sent
                </li>
                <li data-id="element-1219">
                  User confirms → added to:{' '}
                  <strong data-id="element-1220">
                    {selectedNewsletters.map(n => n.name).join(', ') || 'no lists'}
                  </strong>
                </li>
                <li data-id="element-1221">
                  Redirected to:{' '}
                  <strong data-id="element-1222">{config.redirectUrl || 'default page'}</strong>
                </li>
              </ol>
            </div>
          </div>}

        <div className="mt-8 flex justify-between pt-6 border-t border-gray-100" data-id="element-1223">
          <button onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} disabled={currentStep === 1} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1224">
            Back
          </button>

          {currentStep === 5 ? <button onClick={handleSave} disabled={!config.formId || !config.templateId} className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50" data-id="element-1225">
              <Save className="h-4 w-4" data-id="element-1226" />
              Save Configuration
            </button> : <button onClick={() => setCurrentStep(Math.min(5, currentStep + 1))} disabled={currentStep === 1 && !config.formId} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-1227">
              Next Step
              <ArrowRight className="h-4 w-4" data-id="element-1228" />
            </button>}
        </div>
      </div>
    </div>;
}